import { useEffect, useState, useCallback, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faArrowLeft,
    faClock,
    faCalendar,
    faHome,
    faThumbsUp,
    faThumbsDown,
    faArrowRight,
    faLightbulb,
    faTriangleExclamation,
    faCheck,
    faSpinner,
} from '@fortawesome/free-solid-svg-icons'
import { api } from '../../../auth'
import { submitArticleFeedback } from '../api/knowledgeBaseApi'
import type { KbArticleDetail, ContentBlock } from '../types'

interface ArticleViewProps {
    article: KbArticleDetail
    onBack: () => void
    onGoHome: () => void
    onSelectArticle: (slug: string) => void
}

const ContentRenderer = ({ block }: { block: ContentBlock }) => {
    switch (block.type) {
        case 'paragraph':
            return <p className="kb-article__paragraph">{block.text}</p>
        case 'heading':
            return (
                <h2
                    className="kb-article__heading"
                    id={block.text.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
                >
                    {block.text}
                </h2>
            )
        case 'list':
            return (
                <ul className="kb-article__list">
                    {block.items.map((item, i) => (
                        <li key={i}>
                            <FontAwesomeIcon
                                icon={faCheck}
                                className="kb-article__list-icon"
                            />
                            {item}
                        </li>
                    ))}
                </ul>
            )
        case 'ordered-list':
            return (
                <ol className="kb-article__ordered-list">
                    {block.items.map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ol>
            )
        case 'tip':
            return (
                <div className="kb-article__callout kb-article__callout--tip">
                    <div className="kb-article__callout-icon">
                        <FontAwesomeIcon icon={faLightbulb} />
                    </div>
                    <div className="kb-article__callout-content">
                        <strong>Tip</strong>
                        <p>{block.text}</p>
                    </div>
                </div>
            )
        case 'warning':
            return (
                <div className="kb-article__callout kb-article__callout--warning">
                    <div className="kb-article__callout-icon">
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                    </div>
                    <div className="kb-article__callout-content">
                        <strong>Important</strong>
                        <p>{block.text}</p>
                    </div>
                </div>
            )
        case 'code':
            return (
                <div className="kb-article__code-block">
                    <div className="kb-article__code-header">
                        <span>{block.language}</span>
                    </div>
                    <pre>
                        <code>{block.code}</code>
                    </pre>
                </div>
            )
        case 'steps':
            return (
                <div className="kb-article__steps">
                    {block.steps.map((step, i) => (
                        <div key={i} className="kb-article__step">
                            <div className="kb-article__step-number">
                                {i + 1}
                            </div>
                            <div className="kb-article__step-content">
                                <h4>{step.title}</h4>
                                <p>{step.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )
        default:
            return null
    }
}

const ArticleView = ({
    article,
    onBack,
    onGoHome,
    onSelectArticle,
}: ArticleViewProps) => {
    const axiosPrivate = api
    const [readingProgress, setReadingProgress] = useState(0)
    const [feedback, setFeedback] = useState<'up' | 'down' | null>(
        article.user_feedback
    )
    const [feedbackLoading, setFeedbackLoading] = useState(false)
    const articleRef = useRef<HTMLDivElement>(null)

    const headings = article.content.filter((b) => b.type === 'heading') as {
        type: 'heading'
        text: string
    }[]

    const handleScroll = useCallback(() => {
        if (!articleRef.current) return
        const el = articleRef.current
        const scrollTop = window.scrollY - el.offsetTop
        const scrollHeight = el.scrollHeight - window.innerHeight
        const progress = Math.min(
            100,
            Math.max(0, (scrollTop / scrollHeight) * 100)
        )
        setReadingProgress(progress)
    }, [])

    useEffect(() => {
        setFeedback(article.user_feedback)
    }, [article.slug, article.user_feedback])

    useEffect(() => {
        window.addEventListener('scroll', handleScroll)
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [handleScroll])

    const handleFeedback = async (value: 'up' | 'down') => {
        setFeedback(value)
        setFeedbackLoading(true)
        try {
            await submitArticleFeedback(axiosPrivate, article.slug, value)
        } catch {
            setFeedback(article.user_feedback)
        } finally {
            setFeedbackLoading(false)
        }
    }

    const scrollToSection = (text: string) => {
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-')
        document
            .getElementById(id)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    return (
        <section className="kb-article-view" ref={articleRef}>
            <div
                className="kb-article-view__progress"
                style={{ width: `${readingProgress}%` }}
            />

            <div className="kb-article-view__breadcrumb">
                <button onClick={onGoHome} className="kb-breadcrumb__link">
                    <FontAwesomeIcon icon={faHome} className="me-1" />
                    Knowledge Base
                </button>
                <span className="kb-breadcrumb__sep">/</span>
                <button onClick={onBack} className="kb-breadcrumb__link">
                    {article.category.title}
                </button>
                <span className="kb-breadcrumb__sep">/</span>
                <span className="kb-breadcrumb__current">{article.title}</span>
            </div>

            <div className="kb-article-view__layout">
                {headings.length > 0 && (
                    <aside className="kb-article-view__sidebar">
                        <div className="kb-article-view__toc">
                            <h4 className="kb-article-view__toc-title">
                                On this page
                            </h4>
                            <ul className="kb-article-view__toc-list">
                                {headings.map((h) => (
                                    <li key={h.text}>
                                        <button
                                            onClick={() =>
                                                scrollToSection(h.text)
                                            }
                                            className="kb-article-view__toc-link"
                                        >
                                            {h.text}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>
                )}

                <article className="kb-article-view__main">
                    <button onClick={onBack} className="kb-article-view__back">
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Back to {article.category.title}
                    </button>

                    <h1 className="kb-article-view__title">{article.title}</h1>

                    <div className="kb-article-view__meta">
                        <span>
                            <FontAwesomeIcon
                                icon={faClock}
                                className="me-1"
                            />
                            {article.reading_time} min read
                        </span>
                        {article.last_updated && (
                            <span>
                                <FontAwesomeIcon
                                    icon={faCalendar}
                                    className="me-1"
                                />
                                Updated{' '}
                                {new Date(
                                    article.last_updated
                                ).toLocaleDateString('en-GB', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                })}
                            </span>
                        )}
                    </div>

                    <div className="kb-article-view__body">
                        {article.content.map((block, i) => (
                            <ContentRenderer key={i} block={block} />
                        ))}
                    </div>

                    <div className="kb-article-view__feedback">
                        <h3>Was this article helpful?</h3>
                        <div className="kb-article-view__feedback-buttons">
                            <button
                                className={`kb-feedback-btn ${feedback === 'up' ? 'kb-feedback-btn--active' : ''}`}
                                onClick={() => handleFeedback('up')}
                                disabled={feedbackLoading}
                            >
                                <FontAwesomeIcon
                                    icon={
                                        feedbackLoading && feedback === 'up'
                                            ? faSpinner
                                            : faThumbsUp
                                    }
                                    spin={
                                        feedbackLoading && feedback === 'up'
                                    }
                                />
                                <span>Yes, helpful</span>
                            </button>
                            <button
                                className={`kb-feedback-btn ${feedback === 'down' ? 'kb-feedback-btn--active kb-feedback-btn--negative' : ''}`}
                                onClick={() => handleFeedback('down')}
                                disabled={feedbackLoading}
                            >
                                <FontAwesomeIcon
                                    icon={
                                        feedbackLoading && feedback === 'down'
                                            ? faSpinner
                                            : faThumbsDown
                                    }
                                    spin={
                                        feedbackLoading && feedback === 'down'
                                    }
                                />
                                <span>Not really</span>
                            </button>
                        </div>
                        {feedback && !feedbackLoading && (
                            <p className="kb-article-view__feedback-thanks">
                                Thank you for your feedback!
                            </p>
                        )}
                    </div>

                    {article.related_articles.length > 0 && (
                        <div className="kb-article-view__related">
                            <h3>Related Articles</h3>
                            <div className="kb-article-view__related-grid">
                                {article.related_articles.map((rel) => (
                                    <button
                                        key={rel.id}
                                        className="kb-related-card"
                                        onClick={() =>
                                            onSelectArticle(rel.slug)
                                        }
                                    >
                                        <div
                                            className="kb-related-card__accent"
                                            style={{
                                                background:
                                                    rel.category.gradient,
                                            }}
                                        />
                                        <h4>{rel.title}</h4>
                                        <p>{rel.excerpt}</p>
                                        <span className="kb-related-card__link">
                                            Read more{' '}
                                            <FontAwesomeIcon
                                                icon={faArrowRight}
                                                size="xs"
                                            />
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </article>
            </div>
        </section>
    )
}

export default ArticleView
